/**
 * ConsentBanner — GDPR + CCPA + Google Consent Mode v2 compliant.
 *
 * Behaviour:
 *   - On first visit (no stored decision), shows a bottom banner with
 *     "Accept", "Reject", "Preferences" buttons.
 *   - Writes a canonical `ts_consent_v1` object to localStorage:
 *       { ads: true|false, analytics: true|false, ts: <iso> }
 *   - Broadcasts the choice to Google Consent Mode via
 *     `gtag('consent', 'update', {...})` so gtag + AdSense pick it up.
 *   - Fires a `window` event `ts-consent-changed` so components (like AdSlot)
 *     can re-render on decision.
 *
 * Non-goals:
 *   - Not a full CMP. If you need TCF v2.2 for EU programmatic beyond
 *     AdSense (e.g. header bidding, Prebid), drop in Cookiebot or OneTrust
 *     alongside — they'll set the same localStorage key and this banner
 *     stays out of the way.
 */
import { useEffect, useState } from "react";
import { X, Cookie, Check } from "lucide-react";

const KEY = "ts_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeConsent(consent) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...consent, ts: new Date().toISOString() }));
    // Google Consent Mode v2 signals — required for AdSense + GA to serve
    // personalised ads / analytics in the EEA.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: consent.ads ? "granted" : "denied",
        ad_user_data: consent.ads ? "granted" : "denied",
        ad_personalization: consent.ads ? "granted" : "denied",
        analytics_storage: consent.analytics ? "granted" : "denied",
      });
    }
    window.dispatchEvent(new CustomEvent("ts-consent-changed", { detail: consent }));
  } catch {
    // localStorage unavailable (Safari private, etc.) — treat as ephemeral session.
  }
}

// Set defaults BEFORE gtag / AdSense fire so they respect the user's later choice.
// This runs once at module load — safe on the client, no-op on the server.
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  const existing = readConsent();
  const defaults = existing || { ads: false, analytics: false };
  window.gtag("consent", "default", {
    ad_storage: defaults.ads ? "granted" : "denied",
    ad_user_data: defaults.ads ? "granted" : "denied",
    ad_personalization: defaults.ads ? "granted" : "denied",
    analytics_storage: defaults.analytics ? "granted" : "denied",
    wait_for_update: 500,
  });
}

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ ads: true, analytics: true });

  useEffect(() => {
    const decided = readConsent();
    if (!decided) {
      // Slight delay so the banner doesn't compete with LCP paint.
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    writeConsent({ ads: true, analytics: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ ads: false, analytics: false });
    setVisible(false);
  };
  const savePrefs = () => {
    writeConsent(prefs);
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="consent-banner"
      className="fixed left-3 right-3 bottom-3 md:left-4 md:right-4 md:bottom-4 z-[1000] max-w-[720px] md:mx-auto"
    >
      <div
        className="rounded-md p-4 md:p-5 shadow-2xl border border-titans-blue/30"
        style={{
          background: "linear-gradient(180deg, #0B1E33 0%, #0A1A2B 100%)",
          backdropFilter: "blur(8px)",
        }}
      >
        {!showPrefs ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie size={20} className="text-titans-blue shrink-0 mt-0.5" />
              <div className="text-titans-silver text-sm leading-relaxed">
                <div className="font-cond uppercase tracking-[0.18em] text-[10px] text-white font-bold mb-1">
                  Your Privacy Matters
                </div>
                We use cookies to keep the site running, measure how our stories perform, and (with your permission) show relevant ads that help fund independent Titans coverage.{" "}
                <a href="/privacy" className="text-titans-blue underline decoration-dotted">
                  Read the privacy policy →
                </a>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-col md:shrink-0" data-testid="consent-actions">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="consent-accept"
                className="flex-1 md:flex-none bg-titans-red hover:bg-titans-red-hover text-white px-4 py-2 font-cond uppercase tracking-[0.14em] text-xs inline-flex items-center justify-center gap-1.5"
              >
                <Check size={12} /> Accept all
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="consent-reject"
                className="flex-1 md:flex-none border border-titans-silver/40 hover:border-white text-titans-silver hover:text-white px-4 py-2 font-cond uppercase tracking-[0.14em] text-xs"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="consent-preferences"
                className="text-titans-blue hover:text-white font-cond uppercase tracking-[0.14em] text-[10px] px-2 py-2"
              >
                Preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="text-titans-silver text-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="font-cond uppercase tracking-[0.18em] text-[10px] text-white font-bold">
                Cookie preferences
              </div>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                aria-label="Close preferences"
                className="text-titans-silver hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <label className="flex items-start gap-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked
                disabled
                className="mt-1 h-4 w-4 accent-titans-blue opacity-60"
              />
              <span>
                <span className="block text-white font-cond uppercase tracking-wider text-xs">
                  Essential
                </span>
                <span className="block text-titans-silver text-xs leading-snug">
                  Required for the site to work — always on.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid="consent-prefs-analytics">
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-titans-blue"
              />
              <span>
                <span className="block text-white font-cond uppercase tracking-wider text-xs">
                  Analytics
                </span>
                <span className="block text-titans-silver text-xs leading-snug">
                  Helps us understand which stories readers value.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid="consent-prefs-ads">
              <input
                type="checkbox"
                checked={prefs.ads}
                onChange={(e) => setPrefs((p) => ({ ...p, ads: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-titans-blue"
              />
              <span>
                <span className="block text-white font-cond uppercase tracking-wider text-xs">
                  Personalised ads
                </span>
                <span className="block text-titans-silver text-xs leading-snug">
                  Lets our ad partners show relevant ads. Funds independent coverage.
                </span>
              </span>
            </label>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={savePrefs}
                data-testid="consent-save"
                className="bg-titans-red hover:bg-titans-red-hover text-white px-4 py-2 font-cond uppercase tracking-[0.14em] text-xs inline-flex items-center gap-1.5"
              >
                <Check size={12} /> Save choices
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="border border-titans-silver/40 hover:border-white text-titans-silver hover:text-white px-4 py-2 font-cond uppercase tracking-[0.14em] text-xs"
              >
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
